import classNames from "classnames";
import { ChevronDown, ChevronUp } from "lucide-react";

import { Checkbox } from "@/components/ui/checkbox";
import { SimpleTooltip } from "@/components/ui/tooltip";
import type { HeaderConfig } from "@/types/tables";

interface HeaderProps<C extends HeaderConfig> {
  config: C;
  currentSort: { field?: C["field"]; asc?: boolean };
  onSort: (field: C["field"]) => void;
  checkedAll?: boolean;
  onCheckAll?: (checked: boolean) => void;
}

export function Header<C extends HeaderConfig>({
  config,
  currentSort,
  onSort,
  checkedAll,
  onCheckAll,
}: HeaderProps<C>) {
  const field = config.field;
  const isActive = currentSort.field === field;

  // Convert flex justifyContent to Tailwind classes
  const justifyContentClass = {
    "flex-start": "justify-start",
    "flex-end": "justify-end",
    center: "justify-center",
  }[config.justifyContent || "flex-start"];

  let target = config.disableSorting ? (
    <div className={`flex items-center ${justifyContentClass}`}>
      <span>{config.title}</span>
    </div>
  ) : (
    <button
      type="button"
      onClick={() => onSort(field)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onSort(field);
        }
      }}
      className={`flex items-center ${justifyContentClass} cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-sm`}
      role="button"
      tabIndex={0}
    >
      <span className="mr-1">{config.title}</span>
      {isActive ? (
        currentSort.asc ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )
      ) : (
        <ChevronDown className="h-4 w-4" />
      )}
    </button>
  );

  if (config.type === "checkbox") {
    target = (
      <Checkbox
        checked={checkedAll}
        onCheckedChange={(checked) => {
          onCheckAll?.(!!checked);
        }}
      />
    );
  }

  return (
    <th
      className={classNames(
        "header border-none",
        config.widthClass, // Apply Tailwind width class
        {
          "active-sort": isActive,
        },
      )}
      style={{
        maxWidth: config.maxWidth || "auto",
      }}
      data-test-id={`program-${config.field}-column`}
    >
      {config.tooltip ? (
        <SimpleTooltip
          content={<div className="max-w-[200px]">{config.tooltip}</div>}
          side="top"
        >
          <div>{target}</div>
        </SimpleTooltip>
      ) : (
        target
      )}
    </th>
  );
}
